import {
  AspectRatioBox,
  Badge,
  Button,
  IconButton,
  Image,
  Typography
} from '@smartgift/frontend-toolkit';
import styled, { css } from 'styled-components';

import { DEVICE } from '@hero/constants';

const SelectedButton = styled(IconButton)`
  left: 8px;
  position: absolute;
  top: 8px;
  z-index: 3;
`;

const CardGrid = styled.div`
  bottom: 0;
  display: grid;
  grid-template-columns: repeat(${({ itemCount }) => itemCount}, 1fr);
  left: 0;
  position: absolute;
  right: 0;
  top: 0;

  ${Badge.S.Badge} {
    justify-content: center;
    transform: translate(-50%, 10px);
    width: 183px;
  }
`;

const CardGridItem = styled.div`
  background: transparent;
  height: 100%;
  z-index: 3;
`;

const Navigation = styled.div`
  bottom: 8px;
  display: flex;
  gap: 4px;
  left: 50%;
  position: absolute;
  transform: translateX(-50%);
  z-index: 3;

  ${DEVICE.mobile.max} {
    gap: 8px;
  }
`;

const NavigationItem = styled.div`
  background-color: var(--color-bg-strong);
  border-radius: var(--size-border-radius-circle);
  height: 10px;
  transition: all 0.2s ease-in-out;
  width: 10px;

  ${({ active }) =>
    active &&
    css`
      background-color: var(--color-bg-strong2);
      scale: 1.2;
    `}

  ${DEVICE.mobile.max} {
    width: 16px;
    height: 16px;
  }

  &:hover {
    background-color: var(--color-bg-strong2);
    transform: scale(1.2);
  }
`;

const ProductCardDetail = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 16px;
  justify-content: flex-end;
  padding-top: 8px;
`;

const ProductCardTitle = styled(Typography)`
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  color: var(--color-fg-default);
  display: -webkit-box;
  font-size: 16px;
  font-weight: 400;
  height: 48px;
  line-height: 150%;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ProductCardBrandImage = styled.div`
  align-items: center;
  background-color: var(--color-bg-body);
  border-radius: var(--size-border-radius-sm);
  display: flex;
  height: 32px;
  justify-content: center;
  opacity: 0.9;
  padding: 4px;
  position: absolute;
  right: 8px;
  top: 8px;
  z-index: 1;
`;

const ProductCardPrice = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
`;

const ProductCardPriceValue = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`;

const ProductCardPriceLabel = styled.p`
  color: var(--color-fg-default);
  font-size: 16px;
  font-weight: bold;
  line-height: 18px;
  margin-right: 4px;
`;

const ProductCardRegularPriceLabel = styled(Typography)`
  text-decoration: line-through;
`;

const ProductCardImage = styled(AspectRatioBox)`
  border: 1px solid var(--color-border-default);
  border-radius: var(--size-border-radius-md);
  display: flex;
  overflow: hidden;
  position: relative;

  ${Image.S.Image} {
    background: var(--color-bg-default);
    border: 1px solid var(--color-border-default);
    border-radius: var(--size-border-radius-md);
    height: 100%;
    left: 0;
    object-fit: cover;
    position: absolute;
    top: 0;
    width: 100%;

    ${({ isContain }) => {
      return (
        isContain &&
        css`
          object-fit: contain;
        `
      );
    }}
  }
`;

const ProductCardHeader = styled.div`
  overflow: hidden;
  position: relative;
  width: 100%;

  ${Badge.S.Badge} {
    background-color: var(--color-bg-stronger);
    border: none;
    bottom: 25px;
    color: var(--color-fg-inverse-default);
    font-weight: 700;
    justify-content: center;
    left: 50%;
    position: absolute;
    transform: translate(-50%, 5px);
    width: 183px;
  }

  ${Button.S.Button} {
    bottom: 25px;
    justify-content: center;
    left: 50%;
    position: absolute;
    transform: translate(-50%, 5px);
  }
`;

const CheckInputWrapper = styled.div`
  background-color: var(--color-bg-default);
  border: 2px solid var(--color-border-strong);
  border-radius: var(--size-border-radius-circle);
  cursor: pointer;
  display: block;
  height: 24px;
  left: 8px;
  opacity: 0.5;
  position: absolute;
  top: 8px;
  width: 24px;

  #icon-success,
  #icon-close {
    display: none;
    margin-left: -3px;
    margin-top: -3px;
  }

  ${({ isSelected }) =>
    isSelected &&
    css`
      background-color: var(--color-accent-primary-default);
      border: none;
      opacity: 1;

      #icon-success,
      #icon-close {
        margin-left: -2px;
        margin-top: -2px;
      }
    `};
`;

const ProductCard = styled.div`
  background: var(--color-bg-body);
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  position: relative;

  ${({ isTableCard }) =>
    !isTableCard &&
    css`
      &:hover {
        cursor: pointer;
      }

      ${ProductCardHeader} {
        #button-add {
          display: none;
        }
        #button-remove {
          display: block;
        }
      }
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.5;
      pointer-events: none;

      &:hover {
        box-shadow: var(--shadow-s);
        cursor: default;
      }
    `}

  ${({ isSelected }) =>
    isSelected &&
    css`
      &:hover {
        ${CheckInputWrapper} {
          background-color: var(--color-error-default);
          border-radius: var(--size-border-radius-circle);
        }

        #icon-success {
          display: none !important;
        }
        #icon-close {
          display: block;
        }
      }
      ${CheckInputWrapper} {
        #icon-success {
          display: block;
        }
      }
    `}
`;

const ProductStockWrapper = styled.div`
  left: 8px;
  position: absolute;
  top: 8px;
  z-index: 2;

  label {
    font-size: 12px;
    font-weight: 700;
  }
`;

const ProductCardHoverIcon = styled.div`
  background-color: var(--color-bg-body);
  border-radius: var(--size-border-radius-circle);
  box-shadow: var(--shadow-s);
  display: grid;
  height: 48px;
  margin-bottom: 4px;
  place-items: center;
  width: 48px;
`;

export {
  CardGrid,
  CardGridItem,
  CheckInputWrapper,
  Navigation,
  NavigationItem,
  ProductCard,
  ProductCardBrandImage,
  ProductCardDetail,
  ProductCardHeader,
  ProductCardHoverIcon,
  ProductCardImage,
  ProductCardPrice,
  ProductCardPriceLabel,
  ProductCardPriceValue,
  ProductCardRegularPriceLabel,
  ProductCardTitle,
  ProductStockWrapper,
  SelectedButton
};
